/**
 * Quyền của nhân viên trong một đơn vị (Shop / CTV / Đại lý) — docs/KE_HOACH_THANH_VIEN_DON_VI.md.
 * Chủ đơn vị (Membership.role = 'owner') làm được mọi việc như trước, không qua danh mục này. Nhân viên
 * (role = 'staff') chỉ làm được việc có trong vai trò chủ đặt (UnitRole.permissions), và chỉ trong đơn vị đó.
 * Máy chủ chặn từng route theo `config.unit` (apps/api/src/lib/unitAccess.ts) — route chưa khai = chỉ chủ.
 * Hàm thuần, không I/O — web dùng chung để vẽ bộ chọn quyền và ẩn menu / nút.
 */

export const UNIT_PERMISSIONS = [
  'orders.view',
  'orders.create',
  'orders.edit',
  'orders.print',
  'orders.ship_prepay',
  'products.view',
  'products.manage',
  'customers.view',
  'customers.manage',
  'money.view',
  'rates.view',
  'warehouses.manage',
  'network.view',
  'network.manage',
  'network.pricing',
  'network.announce',
] as const
export type UnitPermission = (typeof UNIT_PERMISSIONS)[number]

/** Loại đơn vị — giống Shop.tenantType. */
export type StaffUnitType = 'agency' | 'ctv' | 'shop'

const ALL = new Set<string>(UNIT_PERMISSIONS)

export function isUnitPermission(p: string): p is UnitPermission {
  return ALL.has(p)
}

/** Nhãn + giải thích hiện ở bộ chọn quyền (giữ ngắn — màn điện thoại 358px). */
export const UNIT_PERMISSION_META: Record<UnitPermission, { label: string }> = {
  'orders.view': { label: 'Xem đơn hàng' },
  'orders.create': { label: 'Lên đơn' },
  'orders.edit': { label: 'Sửa / hủy đơn' },
  'orders.print': { label: 'In / tải vận đơn' },
  'orders.ship_prepay': { label: 'Trả tiền ship' },
  'products.view': { label: 'Xem sản phẩm' },
  'products.manage': { label: 'Quản lý sản phẩm' },
  'customers.view': { label: 'Xem khách hàng' },
  'customers.manage': { label: 'Quản lý khách hàng' },
  'money.view': { label: 'Xem tiền & báo cáo' },
  'rates.view': { label: 'Xem bảng giá' },
  'warehouses.manage': { label: 'Kho lấy hàng' },
  'network.view': { label: 'Xem cấp dưới' },
  'network.manage': { label: 'Tạo cấp dưới' },
  'network.pricing': { label: 'Bảng giá & chiết khấu cấp dưới' },
  'network.announce': { label: 'Gửi thông báo' },
}

/** Nhóm trong bộ chọn quyền. Nhóm `network` chỉ hiện với Đại lý / CTV. */
export const UNIT_PERMISSION_GROUPS: readonly { key: string; label: string; permissions: readonly UnitPermission[] }[] = [
  { key: 'orders', label: 'Đơn hàng', permissions: ['orders.view', 'orders.create', 'orders.edit', 'orders.print', 'orders.ship_prepay'] },
  { key: 'catalog', label: 'Sản phẩm & khách hàng', permissions: ['products.view', 'products.manage', 'customers.view', 'customers.manage'] },
  { key: 'money', label: 'Tiền', permissions: ['money.view', 'rates.view'] },
  { key: 'settings', label: 'Cài đặt', permissions: ['warehouses.manage'] },
  { key: 'network', label: 'Mạng lưới', permissions: ['network.view', 'network.manage', 'network.pricing', 'network.announce'] },
]

/** Quyền kéo theo: lên đơn phải chọn được sản phẩm + khách; sửa / in đơn phải xem được đơn… */
const IMPLIES: Partial<Record<UnitPermission, readonly UnitPermission[]>> = {
  'orders.create': ['orders.view', 'products.view', 'customers.view'],
  'orders.edit': ['orders.view'],
  'orders.print': ['orders.view'],
  // Trả ship qua QR thấy số tiền ship phải chuyển = cước theo bảng giá
  'orders.ship_prepay': ['orders.view', 'rates.view'],
  'products.manage': ['products.view'],
  'customers.manage': ['customers.view'],
  'money.view': ['rates.view'],
  'network.manage': ['network.view'],
  // Lập bảng giá cho cấp dưới phải thấy giá mình mua (không bán dưới giá nhập)
  'network.pricing': ['network.view', 'rates.view'],
  // Gửi thông báo cho cấp dưới (docs/KE_HOACH_GUI_THONG_BAO.md): chọn đơn vị nhận phải xem được cấp dưới
  'network.announce': ['network.view'],
}

const NETWORK_ONLY: readonly UnitPermission[] = ['network.view', 'network.manage', 'network.pricing', 'network.announce']

/** Đơn vị có mạng lưới (cấp dưới) — Shop thì không. */
export function hasNetwork(unitType: string | null | undefined): boolean {
  return unitType === 'agency' || unitType === 'ctv'
}

/**
 * Quyền chủ đơn vị cấp được cho nhân viên, theo loại đơn vị (Shop không có quyền mạng lưới) và giao diện đơn vị
 * được bật (`workModes` — cột Shop.workModes; bỏ trống = không lọc theo giao diện).
 */
export function grantableUnitPermissions(unitType: string | null | undefined, workModes?: readonly string[] | null): UnitPermission[] {
  const off = workModes ? permissionsOffByWorkModes(unitType, workModes) : null
  return UNIT_PERMISSIONS.filter((p) => (hasNetwork(unitType) || !NETWORK_ONLY.includes(p)) && !off?.has(p))
}

// ─── Giao diện của đơn vị (docs/KE_HOACH_CHON_GIAO_DIEN_CAP_DUOI.md) ─────────────────────────
// Đại lý / CTV có hai giao diện: Quản lý (mạng lưới) và Bán hàng (như Shop). Cấp trên chọn LÚC TẠO (Shop.workModes),
// không sửa về sau.
// Tắt một giao diện = MỌI người trong đơn vị (chủ lẫn nhân viên) mất các quyền chỉ giao diện đó có — máy chủ chặn
// (apps/api/src/lib/unitAccess.ts), web ẩn. Quyền dùng chung (xem đơn, tiền, bảng giá, kho ở Cài đặt) không bao giờ tắt.

export const WORK_MODES = ['manage', 'sell'] as const
export type WorkMode = (typeof WORK_MODES)[number]

export const WORK_MODE_LABEL: Record<WorkMode, string> = { manage: 'Quản lý', sell: 'Bán hàng' }

/** "Quản lý + Bán hàng" / "Chỉ Quản lý" / "Chỉ Bán hàng" — nhãn ngắn ở danh sách, thông báo. */
export function workModesLabel(modes: readonly WorkMode[]): string {
  if (modes.length === 1) return `Chỉ ${WORK_MODE_LABEL[modes[0]!]}`
  return WORK_MODES.map((m) => WORK_MODE_LABEL[m]).join(' + ')
}

/** Quyền chỉ giao diện đó có. Quyền không có ở đây là quyền dùng chung. */
export const WORK_MODE_PERMISSIONS: Record<WorkMode, readonly UnitPermission[]> = {
  manage: NETWORK_ONLY,
  sell: [
    'orders.create',
    'orders.edit',
    'orders.print',
    'orders.ship_prepay',
    'products.view',
    'products.manage',
    'customers.view',
    'customers.manage',
  ],
}

const SHARED_PERMISSIONS = new Set<string>(UNIT_PERMISSIONS.filter((p) => !WORK_MODES.some((m) => WORK_MODE_PERMISSIONS[m].includes(p))))

/** Giao diện đơn vị dùng được: Shop chỉ Bán hàng; Đại lý / CTV theo cột (rỗng / toàn giá trị lạ = cả hai, như đơn vị cũ). */
export function unitWorkModes(unitType: string | null | undefined, stored?: readonly string[] | null): WorkMode[] {
  if (!hasNetwork(unitType)) return ['sell']
  const picked = WORK_MODES.filter((m) => stored?.includes(m))
  return picked.length ? picked : [...WORK_MODES]
}

/** Lựa chọn gửi lên → giá trị lưu (đúng thứ tự, không trùng); null = không hợp lệ (rỗng / có giá trị lạ). */
export function parseWorkModes(input: readonly string[]): WorkMode[] | null {
  if (!input.length || input.some((m) => !(WORK_MODES as readonly string[]).includes(m))) return null
  return WORK_MODES.filter((m) => input.includes(m))
}

/** Quyền mà các giao diện đang tắt lấy đi. */
export function permissionsOffByWorkModes(unitType: string | null | undefined, stored?: readonly string[] | null): Set<UnitPermission> {
  const on = unitWorkModes(unitType, stored)
  return new Set(WORK_MODES.filter((m) => !on.includes(m)).flatMap((m) => WORK_MODE_PERMISSIONS[m]))
}

/** Route cần quyền nào đó của một giao diện (không qua được chỉ với quyền dùng chung) — mới phải đọc giao diện đơn vị. */
export function specNeedsWorkMode(spec: UnitAccessSpec): boolean {
  if (spec === 'self' || spec === 'member') return false
  return !unitSpecAllows(spec, SHARED_PERMISSIONS)
}

/**
 * Giao diện đang tắt mà `spec` cần — rỗng là qua. Chỉ xét Đại lý / CTV: Shop không có Quản lý từ trước, các route
 * mạng lưới tự chặn Shop với câu báo riêng.
 */
export function workModesBlocking(spec: UnitAccessSpec, unitType: string | null | undefined, stored?: readonly string[] | null): WorkMode[] {
  if (!hasNetwork(unitType) || !specNeedsWorkMode(spec)) return []
  const off = permissionsOffByWorkModes(unitType, stored)
  if (!off.size) return []
  const allowed = new Set<string>(UNIT_PERMISSIONS.filter((p) => !off.has(p)))
  if (unitSpecAllows(spec, allowed)) return []
  const missing = missingUnitPermissions(spec, allowed)
  return WORK_MODES.filter((m) => WORK_MODE_PERMISSIONS[m].some((p) => off.has(p) && missing.includes(p)))
}

/** Các quyền `p` kéo theo (không gồm chính nó) — bộ chọn hiện các ô này đã tick, khoá lại. */
export function impliedBy(p: UnitPermission): readonly UnitPermission[] {
  return IMPLIES[p] ?? []
}

/**
 * Quyền thực có của một vai trò trong đơn vị loại `unitType`: bỏ quyền lạ (dòng cũ / bản khác) và quyền loại đơn
 * vị không có, thêm quyền kéo theo, giữ thứ tự danh mục. Dùng cả lúc lưu lẫn lúc kiểm (đơn vị đổi loại sau đó).
 * `workModes` (lúc kiểm): bỏ thêm quyền của giao diện đơn vị đang tắt — vai trò vẫn lưu nguyên, bật lại là có lại.
 */
export function normalizeUnitPermissions(perms: readonly string[], unitType: string | null | undefined, workModes?: readonly string[] | null): UnitPermission[] {
  const allowed = new Set<UnitPermission>(grantableUnitPermissions(unitType, workModes))
  const out = new Set<UnitPermission>()
  for (const p of perms) {
    if (!isUnitPermission(p) || !allowed.has(p)) continue
    out.add(p)
    for (const q of IMPLIES[p] ?? []) if (allowed.has(q)) out.add(q)
  }
  return UNIT_PERMISSIONS.filter((p) => out.has(p))
}

/** Mẫu nhanh khi tạo vai trò — người dùng vẫn sửa tên, bỏ / thêm quyền. */
export const UNIT_ROLE_TEMPLATES = {
  // Lên đơn cần biết cước ship theo bảng giá để báo phí ship cho khách (báo giá ở form tạo đơn theo quyền rates.view)
  // Form tạo đơn mặc định "Shop trả" ship → chuyển tiền ship qua QR (kéo theo xem bảng giá ship)
  order_entry: { label: 'Lên đơn', permissions: ['orders.create', 'orders.print', 'orders.ship_prepay', 'customers.manage', 'rates.view'] },
  warehouse: { label: 'Kho / đóng gói', permissions: ['orders.view', 'orders.print', 'products.manage', 'warehouses.manage'] },
  accountant: { label: 'Kế toán', permissions: ['orders.view', 'products.view', 'customers.view', 'money.view'] },
  manager: { label: 'Quản lý', permissions: [...UNIT_PERMISSIONS] },
} as const satisfies Record<string, { label: string; permissions: readonly UnitPermission[] }>
export type UnitRoleTemplate = keyof typeof UNIT_ROLE_TEMPLATES

export function unitRoleTemplatePermissions(t: UnitRoleTemplate, unitType: string | null | undefined): UnitPermission[] {
  return normalizeUnitPermissions(UNIT_ROLE_TEMPLATES[t].permissions, unitType)
}

/** Việc chỉ chủ đơn vị làm — không có trong danh mục, không cấp được (trang Nhân viên ghi rõ cho chủ biết). */
export const OWNER_ONLY_FEATURES: readonly string[] = [
  'Thêm nhân viên, đặt vai trò và quyền',
  'Tài khoản ngân hàng nhận tiền',
  'Tên và hồ sơ đơn vị',
  'Hãng vận chuyển, API key',
  'Nâng cấp đơn vị, duyệt nâng cấp của cấp dưới',
  'Xử lý góp ý của cấp dưới',
  'Xóa tài khoản',
]

// ─── Quyền một route cần (apps/api — `config.unit`) ───────────────────────────────────────────

/**
 * 'self'   — chỉ đụng tài khoản của chính người gọi (hồ sơ, mật khẩu, đăng xuất, đổi đơn vị, thông báo…): nhân viên
 *            nào cũng dùng được, kể cả vừa bị gỡ / tạm khoá;
 * 'member' — thông tin chung của đơn vị mọi nhân viên đang làm việc đều cần (tên, thương hiệu…): không cần quyền nào,
 *            nhưng phải còn là nhân viên, không bị khoá, đã đổi mật khẩu tạm;
 * một quyền — cần quyền đó; `{ all }` — cần đủ; `{ any }` — cần ít nhất một.
 */
export type UnitAccessSpec = 'self' | 'member' | UnitPermission | { all: readonly UnitPermission[] } | { any: readonly UnitPermission[] }

/** Quyền còn thiếu để qua `spec` — rỗng là được. `{ any }` thiếu cả nhóm thì trả cả nhóm. */
export function missingUnitPermissions(spec: UnitAccessSpec, perms: ReadonlySet<string>): UnitPermission[] {
  if (spec === 'self' || spec === 'member') return []
  if (typeof spec === 'string') return perms.has(spec) ? [] : [spec]
  if ('all' in spec) return spec.all.filter((p) => !perms.has(p))
  return spec.any.some((p) => perms.has(p)) ? [] : [...spec.any]
}

/** Qua được `spec` không. Nhóm rỗng (`{ any: [] }`, `{ all: [] }`) không bao giờ cho qua — khai sai thì chặn. */
export function unitSpecAllows(spec: UnitAccessSpec, perms: ReadonlySet<string>): boolean {
  if (typeof spec === 'object') {
    if ('any' in spec) return spec.any.some((p) => perms.has(p))
    return spec.all.length > 0 && spec.all.every((p) => perms.has(p))
  }
  return missingUnitPermissions(spec, perms).length === 0
}

// ─── Giới hạn ─────────────────────────────────────────────────────────────────────────────────

export const MAX_STAFF_PER_UNIT = 20
export const MAX_ROLES_PER_UNIT = 20
export const UNIT_ROLE_NAME_MAX = 50
/** Mật khẩu tạm chủ đặt cho nhân viên — như mật khẩu tạo cấp dưới (POST /network/units). */
export const STAFF_TEMP_PASSWORD_MIN = 8
